import {useContext} from 'react';

import {type IntlShape, useIntl} from 'react-intl';

import {FormError} from '@time-webkit/all/atoms/form-error';
import {inviteMembersContext} from '../invite_members_context';

import {type TeamInvitationRole} from '@mattermost/types/teams';

import {ROLES_MAP} from '../consts';

import {type ChannelFieldInvitationError} from '../channel_field_invitation_error';

import styles from './invite_members_form.module.css';
import {ChannelFieldInvitationErrorCode} from './channel_field_invitation_error_code';

export const enum ChannelsErrorType {
    EMPTY = 'empty',
    LIMIT_EXCEEDED = 'LIMIT_EXCEEDED',
}

type Props = {
    id: string;
    role: TeamInvitationRole;
    channelsLimit: number;
    clientErrorCode?: string;
}

export const ChannelFieldErrors = ({id, channelsLimit, clientErrorCode, role}: Props) => {
    const intl = useIntl();
    const {channelError: serverChannelError} = useContext(inviteMembersContext);

    const message = getChannelFieldErrorMessage(intl, role, channelsLimit, serverChannelError, clientErrorCode);

    return (
        <FormError
            id={id}
            className={styles.error}
            show={Boolean(message)}
            message={message}
        />
    );
};

function getChannelFieldErrorMessage(
    intl: IntlShape,
    role: TeamInvitationRole,
    channelsLimit: number,
    serverChannelError: ChannelFieldInvitationError | null,
    clientErrorCode?: string,
) {
    if (serverChannelError?.code === ChannelFieldInvitationErrorCode.API_ARCHIVED_CHANNELS_FOUND) {
        return serverChannelError.getLocalizedMessage(intl);
    }

    if (clientErrorCode === ChannelsErrorType.EMPTY) {
        return intl.formatMessage({
            id: 'invite.members.no_channels_error',
            defaultMessage: 'Select to {count} channels to which the {role} will be added',
        }, {count: channelsLimit, role: intl.formatMessage({id: ROLES_MAP[role].displayName, defaultMessage: role})});
    }

    if (clientErrorCode === ChannelsErrorType.LIMIT_EXCEEDED) {
        return getLimitExceededChannelsMessage(intl, role, channelsLimit);
    }

    return serverChannelError?.getLocalizedMessage(intl);
}

function getLimitExceededChannelsMessage(intl: IntlShape, role: TeamInvitationRole, channelsLimit: number) {
    if (role === 'team_restricted_guest') {
        return intl.formatMessage({
            id: 'invite.members.error.channels_limit_exceeded_guest',
            defaultMessage: 'A guest can only be a member of {count} channels',
        }, {count: channelsLimit});
    }

    return intl.formatMessage({
        id: 'invite.members.error.channels_limit_exceeded_guest_plus',
        defaultMessage: 'A guest + can only be a member of {count} channels',
    }, {count: channelsLimit});
}
